<template>
    <div class="geo-autocomplete-wrapper" style="position: relative;">
        <input
            ref="input"
            type="text"
            :class="classname"
            :placeholder="placeholder"
            :id="id"
            v-model="query"
            @input="onInput"
            @focus="onFocus"
            @blur="onBlur"
            @keydown.enter.prevent="selectFirst"
            autocomplete="off"
        />
        <ul v-if="suggestions.length > 0 && showSuggestions" class="geo-autocomplete-dropdown" style="position: absolute; z-index: 9999; width: 100%; background: white; border: 1px solid #ccc; list-style: none; margin: 0; padding: 0; max-height: 250px; overflow-y: auto;">
            <li
                v-for="(item, index) in suggestions"
                :key="index"
                @mousedown.prevent="selectItem(item)"
                style="padding: 8px 12px; cursor: pointer; border-bottom: 1px solid #eee;"
                @mouseenter="highlightedIndex = index"
                :style="{ backgroundColor: highlightedIndex === index ? '#f0f0f0' : 'white' }"
            >
                <div style="font-size: 14px;">{{ item.label }}</div>
            </li>
        </ul>
    </div>
</template>

<script>
let debounceTimer = null;

export default {
    name: 'GeoAutocomplete',
    props: {
        id: { type: String, default: 'geo-autocomplete' },
        classname: { type: String, default: 'el-input__inner' },
        placeholder: { type: String, default: '' },
        value: { type: String, default: '' },
        types: { type: String, default: 'geocode' },
    },
    data() {
        return {
            query: this.value,
            suggestions: [],
            showSuggestions: false,
            highlightedIndex: -1,
            lastSelected: null,
        };
    },
    watch: {
        value(newVal) {
            this.query = newVal;
        },
    },
    methods: {
        onInput() {
            this.$emit('input', this.query);
            this.highlightedIndex = -1;

            if (debounceTimer) clearTimeout(debounceTimer);

            const q = this.query.trim();
            if (q.length < 2) {
                this.suggestions = [];
                this.showSuggestions = false;
                return;
            }

            debounceTimer = setTimeout(() => {
                this.fetchSuggestions(q);
            }, 350);
        },
        async fetchSuggestions(query) {
            try {
                // Photon (Komoot) — free OSM-based geocoding API
                const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=5&lang=en`;
                const response = await fetch(url);
                const data = await response.json();

                if (data.features && data.features.length > 0) {
                    this.suggestions = data.features.map(f => {
                        const props = f.properties || {};
                        const coords = f.geometry && f.geometry.coordinates ? f.geometry.coordinates : [];
                        return {
                            label: this.formatLabel(props),
                            locality: props.city || props.name || '',
                            latitude: coords.length > 1 ? coords[1] : null,
                            longitude: coords.length > 0 ? coords[0] : null,
                            country: props.country || '',
                            state: props.state || '',
                        };
                    });
                    this.showSuggestions = true;
                } else {
                    this.suggestions = [];
                    this.showSuggestions = false;
                }
            } catch (e) {
                console.error('GeoAutocomplete fetch error:', e);
                this.suggestions = [];
                this.showSuggestions = false;
            }
        },
        formatLabel(props) {
            const parts = [];
            if (props.name) parts.push(props.name);
            if (props.city && props.city !== props.name) parts.push(props.city);
            if (props.state) parts.push(props.state);
            if (props.country) parts.push(props.country);
            return parts.join(', ') || props.name || 'Unknown';
        },
        selectItem(item) {
            this.query = item.locality || item.label;
            this.$emit('input', this.query);
            this.lastSelected = item;

            this.$emit('placechanged', {
                locality: item.locality,
                latitude: item.latitude,
                longitude: item.longitude,
                administrative_area_level_1: item.state,
                country: item.country,
            }, item);

            this.suggestions = [];
            this.showSuggestions = false;
        },
        selectFirst() {
            if (this.suggestions.length > 0) {
                const idx = this.highlightedIndex >= 0 ? this.highlightedIndex : 0;
                this.selectItem(this.suggestions[idx]);
            }
        },
        onFocus() {
            if (this.suggestions.length > 0) {
                this.showSuggestions = true;
            }
        },
        onBlur() {
            // Delay to allow click on suggestion
            setTimeout(() => {
                this.showSuggestions = false;
            }, 200);
        },
    },
};
</script>
